/**
 * WhatsAppPreview Component
 *
 * Realistic WhatsApp chat preview — green app bar, recipient header,
 * incoming message bubble with timestamp + double check marks, and
 * action buttons rendered the way WhatsApp does it.
 *
 * Variables are substituted into the body in real time.
 */

import { MessageCircle, Phone, Video, MoreVertical, ExternalLink, PhoneCall, Reply } from "lucide-react";
import type { WhatsAppTemplate } from "../types";

interface WhatsAppPreviewProps {
  template: WhatsAppTemplate | null;
  bodyVariables: Record<string, string>;
  /** Optional: dynamic field mappings — rendered as example values */
  dynamicFields?: Record<string, string | null>;
  /** Optional: example values for each dynamic field key */
  dynamicExamples?: Record<string, string>;
  headerVariable?: string;
  headerMediaUrl?: string;
  appName?: string;
}

/**
 * Replace {{N}} placeholders with values from the variables map.
 * Unfilled variables are shown as light-grey placeholders so the marketer
 * sees exactly which fields still need to be completed.
 */
function substituteVariables(
  text: string,
  variables: Record<string, string>,
  dynamicFields?: Record<string, string | null>,
  dynamicExamples?: Record<string, string>,
): { text: string; hasPlaceholder: boolean } {
  let hasPlaceholder = false;
  const replaced = text.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    // 1) Static value wins if filled
    const staticVal = variables[n];
    if (staticVal && staticVal.trim()) return staticVal;

    // 2) Dynamic field — render example value
    const dyn = dynamicFields?.[n];
    if (dyn && dynamicExamples?.[dyn]) {
      return dynamicExamples[dyn];
    }

    hasPlaceholder = true;
    return `[variable ${n}]`;
  });
  return { text: replaced, hasPlaceholder };
}

/**
 * Render WhatsApp-style markdown: *bold*, _italic_, ~strikethrough~, `code`.
 * Returns React nodes.
 */
function renderWhatsAppMarkdown(text: string): React.ReactNode {
  // Replace newlines with <br/>, then handle *bold*
  const parts: React.ReactNode[] = [];
  const lines = text.split("\n");
  lines.forEach((line, lineIdx) => {
    let lastIndex = 0;
    const regex = /(\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~|`([^`\n]+)`)/g;
    let match: RegExpExecArray | null;
    let key = 0;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      if (match[2]) {
        parts.push(
          <strong key={`${lineIdx}-${key++}`} className="font-semibold">
            {match[2]}
          </strong>,
        );
      } else if (match[3]) {
        parts.push(
          <em key={`${lineIdx}-${key++}`} className="italic">
            {match[3]}
          </em>,
        );
      } else if (match[4]) {
        parts.push(
          <span key={`${lineIdx}-${key++}`} className="line-through opacity-60">
            {match[4]}
          </span>,
        );
      } else if (match[5]) {
        parts.push(
          <code
            key={`${lineIdx}-${key++}`}
            className="bg-black/5 px-1 rounded text-[12px] font-mono"
          >
            {match[5]}
          </code>,
        );
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }
    if (lineIdx < lines.length - 1) {
      parts.push(<br key={`br-${lineIdx}`} />);
    }
  });
  return parts;
}

export function WhatsAppPreview({
  template,
  bodyVariables,
  dynamicFields,
  dynamicExamples,
  headerVariable,
  headerMediaUrl,
  appName = "Deenex",
}: WhatsAppPreviewProps) {
  const now = new Date();
  const timeStr = now.toLocaleTimeString("es-AR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const hasContent = !!template;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-4">
        <MessageCircle size={16} className="text-emerald-500" />
        <h3 className="font-semibold text-slate-900 text-sm">Vista previa en vivo</h3>
      </div>

      {/* Phone Mockup */}
      <div className="flex justify-center">
        <div className="w-[240px] rounded-[1.7rem] bg-gradient-to-b from-slate-900 to-slate-800 p-2 shadow-xl shadow-slate-900/20 relative">
          {/* Dynamic Island */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-b-2xl z-20" />

          {/* Screen */}
          <div className="bg-[#e5ddd5] rounded-[1.3rem] overflow-hidden min-h-[360px] relative">
            {/* WhatsApp doodle background pattern */}
            <div
              className="absolute inset-0 opacity-[0.04] pointer-events-none"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cpath d='M20 20m-4 0a4 4 0 1 1 8 0a4 4 0 1 1-8 0' fill='%23000'/%3E%3C/svg%3E\")",
              }}
            />

            {/* Top bar (WhatsApp green) */}
            <div className="bg-[#075E54] text-white px-2.5 pt-6 pb-1.5 flex items-center gap-2 relative z-10">
              <div className="w-7 h-7 rounded-full bg-emerald-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                {appName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold leading-tight truncate">
                  {appName}
                </p>
                <p className="text-[9px] text-white/70 leading-tight">
                  en línea
                </p>
              </div>
              <Video size={12} className="text-white/80" />
              <Phone size={11} className="text-white/80" />
              <MoreVertical size={12} className="text-white/80" />
            </div>

            {/* Chat area */}
            <div className="px-2.5 py-3 relative z-10 min-h-[280px] flex flex-col justify-end">
              {hasContent && template ? (
                <div className="space-y-2 animate-fade-in" key={template.id}>
                  {/* Date separator */}
                  <div className="flex justify-center">
                    <span className="text-[10px] bg-white/80 backdrop-blur-sm text-slate-600 px-2.5 py-1 rounded-md shadow-sm">
                      Hoy
                    </span>
                  </div>

                  {/* Incoming message bubble */}
                  <div className="flex">
                    <div
                      className="bg-white rounded-lg rounded-tl-none shadow-sm max-w-[88%] overflow-hidden"
                      style={{
                        boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
                      }}
                    >
                      {/* Header media */}
                      {(template.header?.type === "image" ||
                        template.header?.type === "video" ||
                        template.header?.type === "document") &&
                        headerMediaUrl && (
                          <div className="p-1.5 pb-0">
                            <img
                              src={headerMediaUrl}
                              alt="Header"
                              className="w-full h-32 object-cover rounded-md"
                            />
                          </div>
                        )}

                      {/* Header text */}
                      {template.header?.type === "text" &&
                        template.header.text && (
                          <div className="px-3 pt-2 pb-1">
                            <p className="text-[13px] font-bold text-slate-900 leading-tight">
                              {
                                substituteVariables(template.header.text, {
                                  "1": headerVariable || "",
                                }).text
                              }
                            </p>
                          </div>
                        )}

                      {/* Body */}
                      <div className="px-3 pt-2 pb-1">
                        <div className="text-[13px] text-slate-900 leading-snug whitespace-pre-wrap break-words">
                          {renderWhatsAppMarkdown(
                            substituteVariables(
                              template.body,
                              bodyVariables,
                              dynamicFields,
                              dynamicExamples,
                            ).text,
                          )}
                        </div>
                      </div>

                      {/* Footer */}
                      {template.footer && (
                        <div className="px-3 pb-1">
                          <p className="text-[11px] text-slate-400 leading-snug">
                            {template.footer}
                          </p>
                        </div>
                      )}

                      {/* Timestamp + ticks */}
                      <div className="flex items-center justify-end gap-1 px-3 pb-1.5">
                        <span className="text-[10px] text-slate-400">
                          {timeStr}
                        </span>
                      </div>

                      {/* Quick reply buttons (in-bubble divider) */}
                      {template.buttons &&
                        template.buttons.some(
                          (b) => b.type === "quick_reply",
                        ) && (
                          <div className="border-t border-slate-100">
                            {template.buttons
                              .filter((b) => b.type === "quick_reply")
                              .map((btn, i) => (
                                <div key={`qr-${i}`}>
                                  {i > 0 && (
                                    <div className="border-t border-slate-100" />
                                  )}
                                  <button className="w-full py-2 text-[12px] font-medium text-[#00a884] flex items-center justify-center gap-1.5">
                                    <Reply size={12} />
                                    {btn.label}
                                  </button>
                                </div>
                              ))}
                          </div>
                        )}

                      {/* CTA buttons (URL / Phone) — rendered below bubble in real WA */}
                      {template.buttons &&
                        template.buttons.some(
                          (b) => b.type === "url" || b.type === "phone_number",
                        ) && (
                          <div className="border-t border-slate-100">
                            {template.buttons
                              .filter(
                                (b) =>
                                  b.type === "url" ||
                                  b.type === "phone_number",
                              )
                              .map((btn, i) => (
                                <div key={`cta-${i}`}>
                                  {i > 0 && (
                                    <div className="border-t border-slate-100" />
                                  )}
                                  <button className="w-full py-2 text-[12px] font-medium text-[#00a884] flex items-center justify-center gap-1.5">
                                    {btn.type === "url" ? (
                                      <ExternalLink size={12} />
                                    ) : (
                                      <PhoneCall size={12} />
                                    )}
                                    {btn.label}
                                  </button>
                                </div>
                              ))}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center px-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/60 backdrop-blur-sm flex items-center justify-center mx-auto mb-2">
                      <MessageCircle size={18} className="text-slate-400" />
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Elegí una plantilla para ver
                      <br />
                      cómo queda el mensaje
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Bottom indicator */}
            <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-24 h-1 bg-black/20 rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default WhatsAppPreview;
